// src/components/kuestion/campus/TemplateEditor.jsx
//
// The template editor from the v4 mockup: the template list on the left with
// its pass chip, the selected template on the right with name, time limit,
// the paper's facts and the pass mark (contract v4 §6). Used by both Campus
// and Solo; question selection stays on the existing authoring page.
//
// The pass mark is a percentage, not raw marks, so it survives the paper
// gaining or losing questions; and it is nullable, because a practice
// template legitimately has no verdict. Changing it affects future sittings
// only — each sitting keeps the threshold in force when it started.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { useKuestionApi } from '../useKuestionApi';
import { Loading, Alert, Note } from '../shared/Bits';
import { SearchIcon } from '../shared/Icons';
import { kq } from '../../../config/routes';
import AiPromptBox from '../shared/AiPromptBox';
import { useCampus } from './CampusContext';

export const passLabel = (p) => (p == null ? 'No mark' : `${p}%`);

export const PassChip = ({ value }) => (
  <span className={`kqc-passchip${value == null ? ' is-none' : ''}`}>{passLabel(value)}</span>
);

// Shared by this editor and the authoring page's settings panel.
export function PassMarkControl({ on, value, onToggle, onChange, questionCount }) {
  const n = parseInt(value, 10);
  const valid = !Number.isNaN(n) && n >= 0 && n <= 100;
  const q = Number(questionCount) || 0;
  return (
    <div className="kqc-box" style={{ maxWidth: 620 }}>
      <div className="kqc-box__head">
        <span>Pass mark</span>
        <span className="kqc-spacer" />
        <span className={`kqc-passstate${on ? (valid ? ' is-set' : ' is-bad') : ''}`}>{on ? (valid ? `${n}% to pass` : 'Invalid') : 'Not set'}</span>
      </div>
      <div className="kqc-box__body">
        <div className="kqc-seg" style={{ marginBottom: 16 }}>
          <button type="button" className={!on ? 'is-on' : ''} onClick={() => onToggle(false)} aria-pressed={!on}>No pass mark</button>
          <button type="button" className={on ? 'is-on' : ''} onClick={() => onToggle(true)} aria-pressed={on}>Set a threshold</button>
        </div>
        {on ? (
          <>
            <div className="kqc-row kqc-row--gap" style={{ alignItems: 'flex-end', marginBottom: 16 }}>
              <div className="field" style={{ flex: '0 1 130px' }}>
                <label htmlFor="t-pass">Percentage</label>
                <div className="kqc-affix kqc-affix--suffix">
                  <input id="t-pass" type="text" inputMode="numeric" value={value} onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))} />
                  <span>%</span>
                </div>
              </div>
              <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                <div className="kqc-tiny" style={{ marginBottom: 6 }}>{q ? `On this ${q}-question paper` : 'On this paper'}</div>
                <div className="kqc-mono" style={{ fontSize: 'clamp(17px,2vw,22px)' }}>{valid && q ? `${Math.ceil((n / 100) * q)} of ${q} questions right` : valid ? `${n}% of the questions right` : '—'}</div>
              </div>
            </div>
            <div className="kqc-passbar" style={{ margin: '0 0 8px' }}><b style={{ left: `${Math.min(100, Math.max(0, valid ? n : 0))}%` }} /></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }} className="kqc-small kqc-muted"><span>0%</span><span>100%</span></div>
            <div className={`kqc-small${valid ? ' kqc-muted' : ' kqc-accent-ink'}`}>{valid ? `A student scoring ${n}% or more passes. Below that, fails.` : 'Enter a whole number between 0 and 100.'}</div>
          </>
        ) : (
          <div className="kqc-small" style={{ lineHeight: 1.55, color: 'var(--color-neutral-800)' }}>Sittings report a score and nothing else &mdash; no pass or fail. Right for practice papers, where a verdict would be noise.</div>
        )}
      </div>
    </div>
  );
}

export default function TemplateEditor() {
  const navigate = useNavigate();
  const api = useKuestionApi();
  const [templates, setTemplates] = useState(null);
  const [banks, setBanks] = useState([]);
  const [q, setQ] = useState('');
  const [selId, setSelId] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  // Attaching an imported bank changes the template object; this carries the
  // editor's unsaved fields across that change so an import never discards them.
  const keepForm = useRef(null);

  const load = () => {
    let live = true;
    api.get(API_ENDPOINTS.KQ_TEMPLATES).then((d) => { if (live) setTemplates(d || []); }).catch((e) => { if (live) { setTemplates([]); setFlash({ kind: 'error', msg: e.message }); } });
    api.get(API_ENDPOINTS.KQ_BANKS).then((d) => { if (live) setBanks(d || []); }).catch(() => {});
    return () => { live = false; };
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [api]);

  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    return (templates || []).filter((t) => !n || `${t.template_name || ''} ${t.exam_name || ''}`.toLowerCase().includes(n));
  }, [templates, q]);

  const current = useMemo(() => (templates || []).find((t) => String(t.id) === String(selId)) || (templates && templates[0]) || null, [templates, selId]);

  useEffect(() => {
    if (!current) { setForm(null); return; }
    if (keepForm.current) { setForm(keepForm.current); keepForm.current = null; return; }
    setForm({
      name: current.template_name || current.exam_name || '',
      time: String(current.time_unit === 'hours' ? Math.round((current.time_limit || 0) / 60) : (current.time_limit || '')),
      passOn: current.pass_percentage != null,
      passValue: current.pass_percentage != null ? String(current.pass_percentage) : '70',
    });
  }, [current]);

  // Solo has no organisation; the prompt then simply leaves that line out.
  const { org } = useCampus();
  const moduleNames = useMemo(() => {
    if (!current) return [];
    const ids = new Set((current.selected_banks || []).map(String));
    return [...new Set(banks.filter((b) => ids.has(String(b.id))).map((b) => b.module).filter(Boolean))];
  }, [current, banks]);

  const moduleCount = useMemo(() => {
    if (!current) return 0;
    const ids = new Set((current.selected_banks || []).map(String));
    return new Set(banks.filter((b) => ids.has(String(b.id))).map((b) => b.module)).size;
  }, [current, banks]);

  const passN = form ? parseInt(form.passValue, 10) : NaN;
  const passValid = !form?.passOn || (!Number.isNaN(passN) && passN >= 0 && passN <= 100);
  const dirty = form && current && (
    form.name.trim() !== (current.template_name || current.exam_name || '')
    || String(form.time) !== String(current.time_unit === 'hours' ? Math.round((current.time_limit || 0) / 60) : (current.time_limit || ''))
    || form.passOn !== (current.pass_percentage != null)
    || (form.passOn && passN !== Number(current.pass_percentage))
  );

  const save = async () => {
    if (!current || !form || !passValid || busy) return;
    if (!form.name.trim()) { setFlash({ kind: 'error', msg: 'Give the template a name before saving.' }); return; }
    setBusy(true); setFlash(null);
    const mins = parseInt(form.time, 10) || 0;
    const body = {
      template_name: form.name.trim(),
      exam_name: form.name.trim(),
      total_score: current.total_score,
      time_limit: current.time_unit === 'hours' ? mins * 60 : mins,
      time_unit: current.time_unit || 'minutes',
      shuffle: !!current.shuffle,
      selected_banks: current.selected_banks || [],
      pass_percentage: form.passOn ? passN : null,
    };
    try {
      const updated = await api.putJson(API_ENDPOINTS.KQ_TEMPLATE(current.id), body);
      setTemplates((ts) => ts.map((t) => (t.id === current.id ? { ...t, ...body, ...(updated && updated.id ? updated : {}) } : t)));
      setFlash({ kind: 'success', msg: `Saved. ${form.passOn ? `Future sittings need ${passN}% to pass.` : 'Future sittings report a score only.'}` });
    } catch (e) {
      setFlash({ kind: 'error', msg: e.message });
    } finally {
      setBusy(false);
    }
  };

  // "Import the reply": the assistant's JSON, already checked by the shared
  // validator, becomes a bank on this template. It goes through the same
  // upload as the Question banks page, so the server is still the authority;
  // then the bank is added to the template's selection.
  const importReply = async ({ text, fileName }) => {
    if (!current) return { ok: false, errors: ['Pick a template first.'] };
    const known = new Set(banks.map((b) => String(b.id)));
    const fd = new FormData();
    fd.append('file', new File([text], fileName, { type: 'application/json' }));
    let up;
    try { up = await api.postForm(API_ENDPOINTS.KQ_FILE_UPLOAD, fd); } catch (e) { return { ok: false, errors: [e.message] }; }
    if (up && up.is_valid === false) {
      return { ok: false, errors: Array.isArray(up.validation_errors) && up.validation_errors.length ? up.validation_errors : ['The server did not accept this bank.'] };
    }

    // Which bank did that create? Told directly, or found on the file's row,
    // or the one bank that was not there before.
    let bankId = up?.bank_id ?? up?.bank?.id ?? null;
    let fresh = banks;
    try { fresh = (await api.get(API_ENDPOINTS.KQ_BANKS)) || []; setBanks(fresh); } catch { /* the list refreshes on the next visit */ }
    if (bankId == null && up?.id != null) {
      try { bankId = ((await api.get(API_ENDPOINTS.KQ_FILE_LIST)) || []).find((f) => String(f.id) === String(up.id))?.bank_id ?? null; } catch { /* fall through */ }
    }
    if (bankId == null) {
      const added = fresh.filter((b) => !known.has(String(b.id)));
      if (added.length === 1) bankId = added[0].id;
    }
    if (bankId == null) return { ok: true, attached: false };

    const ids = current.selected_banks || [];
    if (ids.map(String).includes(String(bankId))) return { ok: true, attached: true };
    // Only the selection changes: the saved name, timing and pass mark go back as they are.
    const body = {
      template_name: current.template_name || current.exam_name,
      exam_name: current.exam_name || current.template_name,
      total_score: current.total_score,
      time_limit: current.time_limit,
      time_unit: current.time_unit || 'minutes',
      shuffle: !!current.shuffle,
      selected_banks: [...ids, bankId],
      pass_percentage: current.pass_percentage ?? null,
    };
    try {
      const updated = await api.putJson(API_ENDPOINTS.KQ_TEMPLATE(current.id), body);
      keepForm.current = form;
      setTemplates((ts) => ts.map((t) => (t.id === current.id ? { ...t, selected_banks: body.selected_banks, ...(updated && updated.id ? updated : {}) } : t)));
      return { ok: true, attached: true };
    } catch (e) {
      return { ok: true, attached: false, why: e.message };
    }
  };

  return (
    <div className="kqc-page kqc-page--flush">
      <div className="kqc-split">
        <div className="kqc-split__side" style={{ maxWidth: 380 }}>
          <div className="kqc-split__head">
            <div className="kqc-row" style={{ marginBottom: 12 }}>
              <h1 style={{ fontSize: 19 }}>Templates</h1>
              <span className="kqc-count">{templates ? templates.length : '…'}</span>
              <span className="kqc-spacer" />
              <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate(kq('templates/build'))} data-tour="tpl-new">New</button>
            </div>
            <div className="kqc-search">
              <span><SearchIcon /></span>
              <input type="text" placeholder="Search templates" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
          </div>
          <div className="kqc-split__scroll">
            {templates === null ? <div style={{ padding: '0 20px' }}><Loading label="Loading templates" /></div>
              : list.length === 0 ? <div className="kqc-small kqc-muted" style={{ padding: 20 }}>{templates.length ? 'No templates match.' : 'No templates yet. Build one from your banks.'}</div>
                : list.map((t) => (
                  <button type="button" key={t.id} className={`kqc-roster__row${current && current.id === t.id ? ' is-selected' : ''}`} onClick={() => setSelId(t.id)}>
                    <span className="kqc-roster__body">
                      <span className="kqc-roster__name">{t.template_name || t.exam_name}</span>
                      <span className="kqc-roster__login" style={{ letterSpacing: 0 }}>{t.total_questions} questions &middot; {t.time_limit} {t.time_unit === 'hours' ? 'h' : 'min'}</span>
                    </span>
                    <PassChip value={t.pass_percentage} />
                  </button>
                ))}
          </div>
        </div>

        <div className="kqc-split__main">
          {flash && <Alert kind={flash.kind} onClose={() => setFlash(null)}>{flash.msg}</Alert>}
          {!current || !form ? (
            templates && templates.length === 0 ? (
              <>
                <div className="kqc-kicker" style={{ marginBottom: 8 }}>Templates</div>
                <h2 className="kqc-h1" style={{ marginBottom: 10 }}>No templates yet</h2>
                <p className="kqc-lede" style={{ marginBottom: 22 }}>A template is the paper: which banks it draws from, how many questions, how long, and the pass mark. Build one, then sit it or assign it.</p>
                <button type="button" className="btn btn-primary" onClick={() => navigate(kq('templates/build'))}>Build a template</button>
              </>
            ) : <Loading label="Loading" />
          ) : (
            <>
              <div className="kqc-kicker" style={{ marginBottom: 10 }}>Template</div>
              <h2 className="kqc-h1" style={{ fontSize: 'clamp(24px,3vw,36px)', marginBottom: 22 }}>{form.name || 'Untitled'}</h2>

              <div className="kqc-row kqc-row--gap" style={{ alignItems: 'stretch', marginBottom: 24, maxWidth: 620 }}>
                <div className="field" style={{ flex: '1 1 220px' }}><label htmlFor="t-name">Template name</label><input id="t-name" className="input" type="text" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
                <div className="field" style={{ flex: '0 1 150px' }}><label htmlFor="t-time">Time limit ({current.time_unit === 'hours' ? 'h' : 'min'})</label><input id="t-time" className="input" type="text" inputMode="numeric" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value.replace(/[^0-9]/g, '').slice(0, 4) }))} /></div>
              </div>

              <div className="kqc-stats" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', maxWidth: 620, marginBottom: 28 }}>
                <div className="kqc-stat"><div className="kqc-stat__label">Questions</div><div className="kqc-stat__value kqc-stat__value--sm">{current.total_questions ?? '—'}</div></div>
                <div className="kqc-stat"><div className="kqc-stat__label">Modules</div><div className="kqc-stat__value kqc-stat__value--sm">{banks.length ? moduleCount : '—'}</div></div>
                <div className="kqc-stat"><div className="kqc-stat__label">Shuffle</div><div className="kqc-stat__value kqc-stat__value--sm">{current.shuffle ? 'On' : 'Off'}</div></div>
              </div>

              <div style={{ marginBottom: 22 }}>
                <PassMarkControl
                  on={form.passOn}
                  value={form.passValue}
                  questionCount={current.total_questions}
                  onToggle={(on) => setForm((f) => ({ ...f, passOn: on }))}
                  onChange={(v) => setForm((f) => ({ ...f, passValue: v }))}
                />
              </div>

              <Note style={{ marginBottom: 24, maxWidth: 620 }}>Changing this affects <strong>future sittings only</strong>. Each sitting keeps the threshold that was in force when the student started, so a historical pass never turns into a fail. In proctored mode the student sees no verdict at all &mdash; the mark is for your report.</Note>

              <div style={{ maxWidth: 620, marginBottom: 24 }}>
                <AiPromptBox
                  orgName={org?.display_name}
                  prefix={org?.prefix}
                  name={form.name}
                  questions={current.total_questions}
                  modules={moduleNames}
                  passOn={form.passOn}
                  passValue={form.passValue}
                  onImport={importReply}
                />
              </div>

              <div className="kqc-row">
                <button type="button" className="btn btn-primary" style={{ minHeight: 46 }} disabled={!dirty || !passValid || busy} onClick={save}>{busy ? 'Saving…' : 'Save template'}</button>
                <button type="button" className="btn btn-secondary" style={{ minHeight: 46 }} onClick={() => navigate(kq(`templates/build?id=${current.id}`))}>Edit questions</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
